import {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Table, Tag, Popconfirm, Segmented, Button as AntButton} from "antd";
import dayjs from "dayjs";
import {PageHeader, ModalForm, FontAwesomeIcon} from "~/components";
import {TextAreaField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetPlanRequestsQuery, useResolvePlanRequestMutation} from "~/reduxs/api/memberApiSlice";
import {planColor} from "../util";

const STATUS_TAG = {
	pending: {color: 'gold', label: 'Chờ duyệt'},
	approved: {color: 'green', label: 'Đã duyệt'},
	rejected: {color: 'red', label: 'Từ chối'},
	cancelled: {color: 'default', label: 'User huỷ'},
};

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Modal nhập lý do khi từ chối yêu cầu (tuỳ chọn — user sẽ thấy trong thông báo). */
function RejectModal({open, submitting, onCancel, onSubmit}) {
	const {control, handleSubmit, reset, formState: {errors}} = useForm({defaultValues: {note: ''}});

	return (
		<ModalForm
			open={open}
			icon="fa-light fa-circle-xmark"
			title="Từ chối yêu cầu nâng cấp"
			subtitle="Lý do sẽ được gửi tới thành viên qua thông báo"
			onCancel={() => { reset({note: ''}); onCancel(); }}
			onOk={handleSubmit((v) => onSubmit(v, () => reset({note: ''})))}
			okText="Từ chối"
			loading={submitting}
		>
			<Controller control={control} name="note" render={({field}) => (
				<TextAreaField label="Lý do (tuỳ chọn)" placeholder="VD: chưa xác nhận được thanh toán..." rows={3} errors={errors} {...field} />
			)} />
		</ModalForm>
	);
}

/**
 * Trang "Yêu cầu nâng cấp gói" (khu quản trị) — user gửi từ /subscription; admin duyệt
 * (gán gói ngay) hoặc từ chối kèm lý do. Tách từ modal cũ ở màn Quản lý thành viên.
 */
function PlanRequests() {
	const {notification} = AntdApp.useApp();
	const [status, setStatus] = useState('pending');
	const {data, isLoading} = useGetPlanRequestsQuery(status);
	const [resolveRequest, {isLoading: resolving}] = useResolvePlanRequestMutation();
	const [rejectRow, setRejectRow] = useState(null);

	const items = data?.items || [];

	const doResolve = async (row, action, note = '') => {
		try {
			const res = await resolveRequest({id: row.id, action, note}).unwrap();
			notification.success({message: res?.message || 'Đã xử lý yêu cầu'});
			setRejectRow(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xử lý được yêu cầu.')});
		}
	};

	const columns = [
		{
			title: 'Thành viên',
			key: 'member',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>{r.name || `@${r.username}`}</div>
					<div style={{fontSize: 12, color: '#8c8c8c'}}>@{r.username}{r.email ? ` · ${r.email}` : ''}</div>
				</div>
			),
		},
		{
			title: 'Gói',
			key: 'plan',
			render: (_, r) => (
				<span>
					{r.kind === 'renew' ? (
						<>
							<Tag color="blue">Gia hạn</Tag>
							<Tag color={planColor(r.plan_slug)}>{r.plan_slug}</Tag>
						</>
					) : (
						<>
							{/* Đơn 'downgrade' = thanh toán gói ĐÍCH của lịch hạ gói (tick tạo). */}
							{r.kind === 'downgrade' && <Tag color="orange">Hạ gói</Tag>}
							<Tag color={planColor(r.current_plan)}>{r.current_plan}</Tag>
							<FontAwesomeIcon icon="fa-light fa-arrow-right" style={{margin: '0 4px', color: '#8c8c8c'}} />
							<Tag color={planColor(r.plan_slug)}>{r.plan_slug}</Tag>
						</>
					)}
				</span>
			),
		},
		// Đơn hàng thanh toán VietQR: số tiền + mã (nội dung CK để đối soát) + khách đã báo CK chưa.
		{
			title: 'Thanh toán',
			key: 'payment',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>
						{r.amount > 0 ? `${nf(r.amount)}đ` : '—'}
						{r.code ? <span style={{fontWeight: 400, color: '#8c8c8c'}}> · {r.code}</span> : null}
					</div>
					{r.status === 'pending' && (r.paid_at
						? <Tag color="green" style={{marginTop: 2}}>Khách báo đã CK {dayjs(r.paid_at).format('HH:mm DD/MM')}</Tag>
						: <Tag style={{marginTop: 2}}>Chưa báo thanh toán</Tag>)}
				</div>
			),
		},
		// Cột phụ — ẩn trên mobile
		{title: 'Lời nhắn', dataIndex: 'note', key: 'note', responsive: ['md'], render: (v) => v || '—'},
		{
			title: 'Thời gian', dataIndex: 'created', key: 'created', width: 150, responsive: ['md'],
			render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
		},
		...(status !== 'pending' ? [{
			title: 'Trạng thái', key: 'status', width: 110,
			render: (_, r) => {
				const m = STATUS_TAG[r.status] || {color: 'default', label: r.status};
				return <Tag color={m.color}>{m.label}</Tag>;
			},
		}] : []),
		...(status === 'pending' ? [{
			title: '', key: 'action', align: 'right', width: 190,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 8}}>
					<Popconfirm
						title={r.kind === 'renew'
							? `Xác nhận ĐÃ NHẬN thanh toán và GIA HẠN gói "${r.plan_slug}" cho @${r.username}? (chu kỳ mới — hạn mức làm mới)`
							: r.kind === 'downgrade'
								? `Xác nhận ĐÃ NHẬN thanh toán và CHUYỂN @${r.username} sang gói "${r.plan_slug}" (hạ gói theo lịch — chu kỳ mới)?`
								: `Xác nhận ĐÃ NHẬN thanh toán và kích hoạt gói "${r.plan_slug}" cho @${r.username}?`}
						okText="Đã thanh toán"
						cancelText="Huỷ"
						onConfirm={() => doResolve(r, 'approve')}
					>
						<AntButton type="primary" size="small" loading={resolving}>Đã thanh toán</AntButton>
					</Popconfirm>
					<AntButton danger size="small" onClick={() => setRejectRow(r)}>Từ chối</AntButton>
				</span>
			),
		}] : []),
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-crown"
				title="Yêu cầu nâng cấp gói"
				subtitle="Duyệt hoặc từ chối yêu cầu nâng cấp / gia hạn gói do thành viên gửi."
			/>

			<div style={{marginBottom: 12}}>
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{value: 'pending', label: 'Chờ duyệt'},
						{value: 'approved', label: 'Đã duyệt'},
						{value: 'rejected', label: 'Từ chối'},
						{value: 'cancelled', label: 'User huỷ'},
					]}
				/>
			</div>

			<Table
				columns={columns}
				dataSource={items}
				rowKey="id"
				loading={isLoading}
				size="small"
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 20, hideOnSinglePage: true}}
				locale={{emptyText: status === 'pending' ? 'Không có yêu cầu nào đang chờ duyệt.' : 'Chưa có yêu cầu nào.'}}
			/>

			<RejectModal
				open={Boolean(rejectRow)}
				submitting={resolving}
				onCancel={() => setRejectRow(null)}
				onSubmit={({note}, resetForm) => doResolve(rejectRow, 'reject', note).then(resetForm)}
			/>
		</div>
	);
}

export default PlanRequests;
